import { useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { showPlans } from '~/lib/planGate';

/*
 * The Publish paywall — the "big popup" shown when a user clicks Publish but
 * can't take the site live yet. Publishing is a paid action (enforced server-side
 * in `deploy-eligibility.ts`), and the popup adapts to WHY it's blocked:
 *
 *   reason='subscribe' (free tier)     → "Upgrade to publish"  → plans dialog
 *   reason='pay'       (Starter tier)  → "$1 to publish"       → $1 Dodo checkout
 *
 * The $1 is per CHAT, not per deploy: once paid, that chat's first deploy and
 * every redeploy are free. Controlled by the parent (DeploymentPanel) via
 * `open`/`onClose`; the reason comes from the tier / the server's refusal code.
 *
 * Styling stays inside the dark-editorial lime system (uno.config.ts): near-black
 * surface, single electric-lime accent, editorial serif display. The "flash" is
 * motion — an aurora glow, twinkling sparkles, a glowing medallion and a CTA
 * shine — all disabled under prefers-reduced-motion.
 */

export type PublishGateReason = 'subscribe' | 'pay';

/** Fixed positions for the drifting sparkle particles around the card. */
const SPARKLES = [
  { top: '8%', left: '10%', size: 13, delay: 0 },
  { top: '16%', left: '86%', size: 10, delay: 0.5 },
  { top: '72%', left: '6%', size: 11, delay: 1.1 },
  { top: '84%', left: '90%', size: 14, delay: 0.8 },
  { top: '44%', left: '94%', size: 9, delay: 1.6 },
  { top: '52%', left: '3%', size: 9, delay: 2.0 },
];

function FeatureRow({ children, index, animate }: { children: React.ReactNode; index: number; animate: boolean }) {
  return (
    <motion.li
      className="flex items-center gap-2.5 text-[13px] leading-tight text-secondary"
      initial={animate ? { opacity: 0, x: -8 } : false}
      animate={animate ? { opacity: 1, x: 0 } : undefined}
      transition={{ delay: 0.18 + index * 0.07, type: 'spring', stiffness: 340, damping: 26 }}
    >
      <span className="grid h-5 w-5 flex-shrink-0 place-items-center rounded-full bg-accent/15 text-accent">
        <span className="i-ph:check-bold text-[11px]" />
      </span>
      <span>{children}</span>
    </motion.li>
  );
}

/**
 * PAUSED 2026-09-04: the Starter "$1 per chat to publish" unlock.
 *
 * Every paid plan publishes and republishes for free now, so this modal only
 * ever renders its free-plan variant. Flip this to `true` and uncomment
 * `payToPublish`, the `busy` state, the `toast` import and the paid variant's
 * markup below to bring the charge back — the server side is the matching block
 * in `deploy-eligibility.ts`.
 */
const PUBLISH_UNLOCK_ENABLED = false;

export function PublishGateModal({
  open,
  reason,
  chatId: _chatId,
  onClose,
}: {
  open: boolean;
  reason: PublishGateReason;
  chatId: string | null;
  onClose: () => void;
}) {
  const reducedMotion = useReducedMotion();
  const flashy = !reducedMotion;

  // PAUSED with the $1 unlock: only the checkout button needed a busy state.
  // const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /*
   * PAUSED 2026-09-04: the $1 publish-unlock checkout. Every paid plan now
   * deploys for free, so nothing sends a user to hosted checkout. Kept
   * commented rather than deleted — see deploy-eligibility.ts for the full note
   * and how to restore the charge.
   *
   * import { apiStartPublishUnlockCheckout } from '~/lib/api/deployments';
   *
   * const payToPublish = async () => {
   *   if (!chatId) {
   *     return;
   *   }
   *
   *   setBusy(true);
   *
   *   try {
   *     const { url } = await apiStartPublishUnlockCheckout(chatId);
   *     // Redirecting to hosted checkout — leave the modal up so the page doesn't flash.
   *     window.location.href = url;
   *   } catch {
   *     setBusy(false);
   *     toast.error('Could not start checkout — please try again.');
   *   }
   * };
   */

  const subscribe = () => {
    onClose();
    showPlans('publish_gate');
  };

  /*
   * PAUSED 2026-09-04: `reason='pay'` (Starter's $1 per-chat unlock). Nothing
   * sets it any more — the free-plan variant is the only one that renders — but
   * the prop and the paid variant's markup below stay so restoring the charge is
   * a one-line change back to `reason === 'pay'`.
   */
  const isPay = PUBLISH_UNLOCK_ENABLED && reason === 'pay';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          // Scrolls when the card is taller than the screen (landscape phone, keyboard up);
          // `safe center` keeps the top reachable instead of clipping it.
          className="fixed inset-0 z-[80] flex items-center [align-items:safe_center] justify-center overflow-y-auto overscroll-contain p-3 sm:p-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Scrim with a lime bloom behind the card */}
          <div
            className="fixed inset-0 bg-black/65 backdrop-blur-[4px]"
            style={{
              backgroundImage: 'radial-gradient(680px 420px at 50% 42%, rgba(217,242,106,0.12), transparent 72%)',
            }}
            onClick={onClose}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={isPay ? 'Pay to publish' : 'Publishing requires a plan'}
            className="relative w-[min(468px,100%)] overflow-hidden rounded-[24px] border border-accent/40 bg-surface px-5 pb-6 pt-8 sm:px-8 sm:pb-7 sm:pt-9 text-center"
            style={{
              backgroundImage: 'linear-gradient(180deg, rgba(217,242,106,0.09), transparent 30%)',
              boxShadow:
                '0 0 0 1px rgba(217,242,106,0.12), 0 44px 120px -40px rgba(217,242,106,0.34), 0 30px 80px -50px rgba(0,0,0,0.9)',
            }}
            initial={flashy ? { opacity: 0, scale: 0.9, y: 16 } : { opacity: 0 }}
            animate={flashy ? { opacity: 1, scale: 1, y: 0 } : { opacity: 1 }}
            exit={flashy ? { opacity: 0, scale: 0.95, y: 8 } : { opacity: 0 }}
            transition={{ type: 'spring', stiffness: 360, damping: 28 }}
          >
            {/* Aurora glow — a slow-breathing lime bloom behind the content */}
            {flashy && (
              <motion.div
                aria-hidden
                className="pointer-events-none absolute -top-24 left-1/2 h-56 w-[130%] -translate-x-1/2"
                style={{ background: 'radial-gradient(closest-side, rgba(217,242,106,0.22), transparent 72%)' }}
                animate={{ opacity: [0.45, 0.85, 0.45], scale: [0.92, 1.06, 0.92] }}
                transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
              />
            )}

            {/* Twinkling sparkles scattered around the card */}
            {flashy &&
              SPARKLES.map((s, i) => (
                <motion.span
                  key={i}
                  aria-hidden
                  className="i-ph:sparkle-fill pointer-events-none absolute text-accent"
                  style={{ top: s.top, left: s.left, fontSize: s.size }}
                  initial={{ opacity: 0, scale: 0.4 }}
                  animate={{ opacity: [0, 1, 0], scale: [0.4, 1, 0.4], rotate: [0, 25, 0] }}
                  transition={{ duration: 2.6, repeat: Infinity, delay: s.delay, ease: 'easeInOut' }}
                />
              ))}

            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-4 top-3.5 z-10 border-0 bg-transparent p-1 text-[14px] text-muted transition-colors hover:text-primary cursor-pointer"
            >
              <span className="i-ph:x-bold" />
            </button>

            <div className="relative">
              {/* Glowing medallion */}
              <div className="relative mx-auto mb-5 grid h-16 w-16 place-items-center">
                {flashy && (
                  <motion.div
                    aria-hidden
                    className="absolute inset-0 rounded-full bg-accent/25 blur-xl"
                    animate={{ opacity: [0.4, 0.85, 0.4], scale: [0.85, 1.12, 0.85] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                  />
                )}
                <div className="relative grid h-16 w-16 place-items-center rounded-full border border-accent/45 bg-accent/[0.08]">
                  <span className="i-ph:rocket-launch-fill text-[27px] text-accent" />
                </div>
              </div>

              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-accent/25 bg-accent/[0.06] px-3 py-1 font-geist-mono text-[10.5px] uppercase tracking-[0.15em] text-accent">
                <span className="i-ph:sparkle-fill text-[10px]" />
                {isPay ? 'Starter plan' : 'Publishing requires a plan'}
              </div>

              {/*
               * PAUSED 2026-09-04: the Starter "$1 to publish" variant. Every paid
               * plan deploys for free now, so only the free-plan variant below can
               * render. Kept rather than deleted so the charge can come back with
               * its design intact — see deploy-eligibility.ts.
               *
               * <>
               *   <h2 className="ui-serif m-0 mb-3 text-[48px] leading-[1.02] text-primary">
               *     Just <span className="text-accent">$1</span> to <em className="italic text-accent">publish</em>
               *   </h2>
               *
               *   <p className="mx-auto mb-5 max-w-[40ch] text-[13.5px] leading-[1.55] text-secondary">
               *     You’re on the Starter plan, so taking a site live is a{' '}
               *     <b className="font-semibold text-accent">one-time $1 per project</b> — pay once and it’s yours to
               *     republish for free.
               *   </p>
               *
               *   <ul className="mx-auto mb-6 flex max-w-[30ch] list-none flex-col gap-2.5 text-left">
               *     <FeatureRow index={0} animate={flashy}>
               *       This project goes live on the web instantly
               *     </FeatureRow>
               *     <FeatureRow index={1} animate={flashy}>
               *       Unlimited free republishes, forever
               *     </FeatureRow>
               *     <FeatureRow index={2} animate={flashy}>
               *       Need more projects? Upgrade to Growth, Pro or Scale
               *     </FeatureRow>
               *   </ul>
               *
               *   <PrimaryButton onClick={payToPublish} disabled={busy || !chatId} busy={busy} flashy={flashy}>
               *     Pay $1 &amp; publish now
               *   </PrimaryButton>
               *
               *   <button
               *     type="button"
               *     onClick={subscribe}
               *     className="mt-3.5 border-0 bg-transparent text-[12.5px] text-muted transition-colors hover:text-secondary cursor-pointer"
               *   >
               *     Compare plans
               *   </button>
               * </>
               */}
              <>
                <h2 className="ui-serif m-0 mb-3 text-[40px] leading-[1.05] text-primary">
                  Upgrade to <em className="italic text-accent">publish</em>
                </h2>

                <p className="mx-auto mb-5 max-w-[40ch] text-[13.5px] leading-[1.55] text-secondary">
                  Publishing isn’t available on the free plan. Choose{' '}
                  <b className="font-semibold text-accent">Plus or Pro</b> to take your site live — building and
                  previewing stay free in the editor.
                </p>

                <ul className="mx-auto mb-6 flex max-w-[30ch] list-none flex-col gap-2.5 text-left">
                  <FeatureRow index={0} animate={flashy}>
                    Publish unlimited projects to the web
                  </FeatureRow>
                  <FeatureRow index={1} animate={flashy}>
                    Connect your own custom domain
                  </FeatureRow>
                  <FeatureRow index={2} animate={flashy}>
                    Remove the “Made with Dunea” badge
                  </FeatureRow>
                  <FeatureRow index={3} animate={flashy}>
                    More monthly credits to build with
                  </FeatureRow>
                </ul>

                <PrimaryButton onClick={subscribe} disabled={false} busy={false} flashy={flashy}>
                  See plans &amp; go live
                </PrimaryButton>

                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3.5 border-0 bg-transparent text-[12.5px] text-muted transition-colors hover:text-secondary cursor-pointer"
                >
                  Maybe later
                </button>
              </>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Accent CTA with a periodic diagonal shine sweep (motion-only flash). */
function PrimaryButton({
  onClick,
  disabled,
  busy,
  flashy,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  busy: boolean;
  flashy: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="ui-btn-accent relative w-full justify-center overflow-hidden py-3 text-[14px] font-semibold"
    >
      {flashy && !disabled && (
        <motion.span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 w-1/3 -skew-x-12"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)' }}
          initial={{ left: '-40%' }}
          animate={{ left: ['-40%', '140%'] }}
          transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 1.4, ease: 'easeInOut' }}
        />
      )}
      <span className="relative inline-flex items-center gap-2">
        {busy ? (
          <span className="i-svg-spinners:90-ring-with-bg text-[16px]" />
        ) : (
          <>
            {children}
            <span className="i-ph:arrow-right-bold text-[13px]" />
          </>
        )}
      </span>
    </button>
  );
}
